import { useSlimAccount } from "@/api/hooks/account";
import { useDeleteGeoJsonFile, useGetCustomGeoJsonList, useUpdateGeoJsonLabel } from "@/api/hooks/customGeojson";
import { fetchTab } from "@/api/hooks/tab";
import { LayerKindName } from "@/layers/utility/createLayerObject";
import { Button } from "@/overlay/components/CommonParts/buttons";
import SvgInboxIcon from "@/overlay/components/GeoJSONManage/icons/inbox.module.svg";
import styles from "@/overlay/components/GeoJSONManage/style.module.scss";
import { DeleteConfirmationModal } from "@/overlay/components/window/DeleteConfirmationModal";
import { useAppDispatch } from "@/reducer";
import { useTab } from "@/reducer/TabsState";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import Logger from "@mm/logging";
import type { ModelFile as CustomGeoJSONFile } from "@mm/metx-workbench.meteomatics.com";
import type { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { DateTime } from "luxon";
import React, { useCallback, useState } from "react";

const logger = Logger.fromFilename(__filename);

export function GeoJSONFilesTable() {
  const { data: tab } = useTab();
  const dispatch = useAppDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [customGeojsonForDeleteConfirmation, setcustomGeojsonForDeleteConfirmation] =
    useState<CustomGeoJSONFile | null>(null);
  const { list, isLoading, error } = useGetCustomGeoJsonList();
  const { mutate: updateLabel } = useUpdateGeoJsonLabel();
  const { mutate: deleteFile } = useDeleteGeoJsonFile();
  const { data: slimAccount } = useSlimAccount();

  const handleCellValueChanged = (params: any) => {
    const { oldValue, newValue, data } = params;
    if (oldValue !== newValue) {
      updateLabel({ fileId: data.id, newLabel: newValue });
    }
  };

  const handleDelete = useCallback(() => {
    if (!customGeojsonForDeleteConfirmation) {
      logger.warn("No fileId was found.");
      return;
    }

    deleteFile(customGeojsonForDeleteConfirmation.user_file_id, {
      onSuccess: () => {
        if (!tab) {
          return;
        }

        dispatch(fetchTab({ tabId: tab.id }));
      },
    });
  }, [customGeojsonForDeleteConfirmation, deleteFile, dispatch, tab]);

  const columnDefs: ColDef<CustomGeoJSONFile>[] = [
    {
      headerName: t`Date`,
      field: "created_at",
      sortable: true,
      flex: 1,
      filter: true,
      maxWidth: 150,
      valueFormatter: (params) => {
        const dt = DateTime.fromISO(params.value);
        return dt.toLocaleString(DateTime.DATE_SHORT);
      },
      comparator: (valueA, valueB) => {
        const dtA = DateTime.fromISO(valueA);
        const dtB = DateTime.fromISO(valueB);
        return dtA.toMillis() - dtB.toMillis();
      },
    },
    {
      headerName: t`File name`,
      editable: true,
      field: "label",
      sortable: true,
      flex: 1,
      minWidth: 100,
      filter: true,
      onCellValueChanged: handleCellValueChanged,
    },
    {
      headerName: t`Size`,
      field: "size_mb",
      flex: 1,
      maxWidth: 150,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<CustomGeoJSONFile, any>) => {
        const { value, data } = params;
        if (value === null || !data) {
          return "–";
        }
        const sizeText = `${value.toFixed(2)} MB`;
        const isShared = slimAccount && slimAccount.id !== data.owner_account_id;
        return isShared ? `${sizeText} (${t`shared`})` : sizeText;
      },
    },
    {
      headerName: t`Actions`,
      cellRenderer: (params: ICellRendererParams<CustomGeoJSONFile>) => {
        const rowData = params.data;
        return (
          <div className={styles["geojson__table-actions-cell"]}>
            <Button
              className={styles["geojson__table-delete-button"]}
              onClick={() => {
                if (!rowData) {
                  logger.warn("No row data was found.");
                  return;
                }
                setcustomGeojsonForDeleteConfirmation(rowData);
                setIsDeleteModalOpen(true);
              }}
              color={"red"}
              style={{ cursor: "pointer" }}
              data-testid="layer-delete-button"
              icon="delete"
            />
          </div>
        );
      },
      flex: 1,
      sortable: false,
      filter: false,
      minWidth: 100,
      maxWidth: 120,
    },
  ];

  if (isLoading) {
    return (
      <div className={styles["geojson__table-loading"]}>
        <mm-loader size="large" />
      </div>
    );
  }
  if (error) {
    return (
      <div>
        {t`Error on load`}: {error.message}
      </div>
    );
  }

  if (list?.length === 0) {
    return <EmptyList />;
  }

  return (
    <div className={classNames("ag-theme-alpine", styles.geojson__table)}>
      <AgGridReact<CustomGeoJSONFile>
        rowData={list ?? []}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        noRowsOverlayComponent={EmptyList}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        itemName={customGeojsonForDeleteConfirmation?.label ?? undefined}
        onConfirm={() => {
          setIsDeleteModalOpen(false);
          handleDelete();
        }}
      />
    </div>
  );
}

function EmptyList() {
  return (
    <div className={classNames(styles["geojson__files-list"], styles["geojson__files-list--empty"])}>
      <SvgInboxIcon />
      <div>{t`Please upload GeoJSON files`}</div>
    </div>
  );
}
